<template>
  <div class="coupon-batch-page">
    <page-back-title title="批量派券" large />
    <a-card :bordered="false">
      <div class="section-title">选择派券信息</div>
      <a-form-model
        ref="form"
        :model="form"
        :label-col="labelCol"
        :wrapper-col="wrapperCol"
      >
        <a-form-model-item label="选择派发的券">
          <a-select
            v-model="form.couponCode"
            placeholder="请选择"
            class="field-lg"
            show-search
            option-filter-prop="children"
            :loading="couponLoading"
          >
            <a-select-option
              v-for="item in couponOptions"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item label="派发用户">
          <a-select v-model="form.userScope" class="field-scope">
            <a-select-option :value="1">指定用户</a-select-option>
          </a-select>
          <template v-if="form.userScope === 1">
            <a-select
              v-model="form.dispatchType"
              class="field-upload-type"
              @change="onDispatchTypeChange"
            >
              <a-select-option :value="1">批量上传领券用户</a-select-option>
              <a-select-option :value="2">指定用户分组</a-select-option>
              <a-select-option :value="3">指定员工认证</a-select-option>
              <a-select-option :value="4">指定大学生认证</a-select-option>
            </a-select>
            <template v-if="form.dispatchType === 1">
              <a-upload
                :file-list="fileList"
                :before-upload="beforeUpload"
                :remove="handleRemove"
              >
                <a-button>上传附件</a-button>
              </a-upload>
              <a class="template-link" @click="handleDownloadTemplate">下载模板，每次最多1万个</a>
            </template>
            <template v-else-if="currentSelectDialog">
              <a-button @click="openSelectDialog(currentSelectDialog.key)">
                {{ currentSelectDialog.buttonText }}
              </a-button>
              <span
                v-if="form[currentSelectDialog.displayField]"
                class="group-name"
              >
                {{ form[currentSelectDialog.displayField] }}
              </span>
            </template>
          </template>
        </a-form-model-item>

        <a-form-model-item label="自动派发">
          <a-radio-group v-model="form.autoDistributeType" @change="onAutoDistributeTypeChange">
            <a-radio :value="0">仅本次派发</a-radio>
            <a-radio :value="1">已派券核销后自动派券</a-radio>
          </a-radio-group>
          <template v-if="form.autoDistributeType === 1">
            <span class="max-quantity-label">累计最多派券</span>
            <a-input-number
              v-model="form.maxQuantity"
              class="field-max-quantity"
              :precision="0"
              placeholder="请输入"
            />
            <span class="max-quantity-unit">次</span>
            <span class="max-quantity-tip">（1~100，-1 为不限制）</span>
          </template>
        </a-form-model-item>
      </a-form-model>
    </a-card>

    <div class="footer-bar">
      <a-button @click="handleCancel">取消</a-button>
      <a-button type="primary" :loading="submitting" @click="handleSubmit">提交</a-button>
    </div>

    <selected-product-dialog
      v-for="dialog in selectDialogConfigs"
      :key="dialog.key"
      :show="activeSelectDialog === dialog.key"
      :title="dialog.title"
      :width="dialog.width"
      :col="2"
      :itemNames="getDialogItemNames(dialog)"
      :query-params="selectDialogQueryParams[dialog.key]"
      :request-promise="(params) => loadSelectDialogList(dialog.key, params)"
      :columns="dialog.columns"
      :row-key="dialog.rowKey"
      :custom-pagination="dialog.customPagination"
      @close="activeSelectDialog = null"
    >
      <a-form-item
        v-for="field in dialog.searchFields"
        :key="field.slot"
        :slot="field.slot"
        :label="field.label"
        :labelCol="{ span: 6 }"
        :wrapperCol="{ span: 18 }"
      >
        <a-input
          v-model="selectDialogQueryParams[dialog.key][field.key]"
          style="width: 100%"
          :placeholder="field.placeholder"
        />
      </a-form-item>
      <template slot="action" slot-scope="rowItem">
        <a-button type="primary" @click="onSelectDialogRecord(dialog, rowItem.record)">选择</a-button>
      </template>
      <template
        v-if="dialog.cellSlots.includes('certStatus')"
        slot="certStatus"
        slot-scope="rowItem"
      >
        <span>{{ formatDialogCell('certStatus', rowItem.record) }}</span>
      </template>
    </selected-product-dialog>
  </div>
</template>

<script>
import SelectedProductDialog from '@/customComponents/SelectedProductDialog/SelectedProductDialog.vue'
import downLoadHelper from '@/utils/helper/downLoadHelper'
import PageBackTitle from './components/PageBackTitle.vue'
import {
  SELECT_DIALOG_KEY,
  createSelectDialogQueryParams,
  getSelectDialogConfigByDispatchType,
  selectDialogConfigs
} from './config/couponBatchSelectDialog.config'

export default {
  name: 'CouponBatchDispatch',
  components: {
    SelectedProductDialog,
    PageBackTitle
  },
  data () {
    return {
      labelCol: { span: 3 },
      wrapperCol: { span: 18 },
      selectDialogConfigs,
      activeSelectDialog: null,
      selectDialogQueryParams: createSelectDialogQueryParams(),
      couponLoading: false,
      submitting: false,
      couponOptions: [],
      fileList: [],
      form: {
        couponCode: this.$route.query.couponCode || undefined,
        userScope: 1,
        dispatchType: 1,
        groupId: '',
        groupName: '',
        enterpriseId: '',
        enterpriseName: '',
        studentId: '',
        studentName: '',
        autoDistributeType: 0,
        maxQuantity: 1
      }
    }
  },
  computed: {
    currentSelectDialog () {
      return getSelectDialogConfigByDispatchType(this.form.dispatchType)
    }
  },
  created () {
    this.loadCouponOptions()
  },
  methods: {
    getDialogItemNames (dialog) {
      return dialog.searchFields.map(item => item.slot)
    },
    formatDialogCell (slotName, record) {
      if (slotName === 'certStatus') {
        return record.certStatus ? '有效' : '无效'
      }
      return record[slotName]
    },
    openSelectDialog (key) {
      this.activeSelectDialog = key
    },
    onSelectDialogRecord (dialog, record) {
      this.form[dialog.idField] = record[dialog.recordIdKey]
      this.form[dialog.displayField] = dialog.getDisplayName(record)
      this.activeSelectDialog = null
    },
    loadSelectDialogList (key, params) {
      if (key === SELECT_DIALOG_KEY.GROUP) {
        return this.loadUserGroupList(params)
      }
      if (key === SELECT_DIALOG_KEY.ENTERPRISE) {
        return this.loadEnterpriseList(params)
      }
      if (key === SELECT_DIALOG_KEY.STUDENT) {
        return this.loadStudentList(params)
      }
      return Promise.resolve({ data: [], totalCount: 0 })
    },
    loadCouponOptions () {
      this.couponLoading = true
      this.getHttpServices()
        .getNewServeService()
        .post('/coupon/page', {
          couponCode: '',
          distributeWay: 5,
          status: 1,
          limitDto: { pageNum: 1, pageSize: 1000 }
        })
        .then(res => {
          const list = (res && res.data) || []
          this.couponOptions = list.map(item => ({
            value: item.couponCode,
            label: item.couponName || ''
          }))
          if (!this.form.couponCode && this.couponOptions.length) {
            this.form.couponCode = this.couponOptions[0].value
          }
        })
        .catch(err => {
          this.$message.error((err && err.msg) || '获取优惠券列表失败')
        })
        .finally(() => {
          this.couponLoading = false
        })
    },
    onDispatchTypeChange () {
      this.fileList = []
      this.form.groupId = ''
      this.form.groupName = ''
      this.form.enterpriseId = ''
      this.form.enterpriseName = ''
      this.form.studentId = ''
      this.form.studentName = ''
      this.activeSelectDialog = null
    },
    onAutoDistributeTypeChange () {
      if (this.form.autoDistributeType === 0) {
        this.form.maxQuantity = 1
      } else if (!this.form.maxQuantity && this.form.maxQuantity !== -1) {
        this.form.maxQuantity = 1
      }
    },
    beforeUpload (file) {
      this.fileList = [file]
      return false
    },
    handleRemove () {
      this.fileList = []
    },
    getUploadFile () {
      const file = this.fileList[0]
      if (!file) return null
      return file.originFileObj || file
    },
    handleDownloadTemplate () {
      const token = this.$store.getters.token || ''
      const url = `${this.getAppConfig().newServeUrl}/couponMember/exportMemberTemplate?token=${encodeURIComponent(token)}`
      downLoadHelper.downloadImage('批量派券用户模板.xlsx', url)
    },
    loadUserGroupList (params) {
      const query = this.selectDialogQueryParams[SELECT_DIALOG_KEY.GROUP] || {}
      const param = {
        pageNum: params.limitDto?.pageNum || 1,
        pageSize: params.limitDto?.pageSize || 10,
        name: params.name || query.name || ''
      }
      return this.getHttpServices().getUserService().get('api/v2/member/tag/list', param)
    },
    loadEnterpriseList (params) {
      const query = this.selectDialogQueryParams[SELECT_DIALOG_KEY.ENTERPRISE] || {}
      return this.getHttpServices()
        .getMemberAuthService()
        .post('member/v3/auth/getEnterpriseList', {
          enterpriseFullName: params.enterpriseFullName || query.enterpriseFullName || '',
          limitDto: {
            pageNum: params.limitDto?.pageNum || 1,
            pageSize: params.limitDto?.pageSize || 10
          }
        })
    },
    loadStudentList (params) {
      const query = this.selectDialogQueryParams[SELECT_DIALOG_KEY.STUDENT] || {}
      return this.getHttpServices()
        .getMemberAuthService()
        .post('/authStudent/page', {
          name: params.name || query.name || '',
          phone: params.phone || query.phone || '',
          merchantId: this.$store.getters.userInfo.merchantId,
          platformId: this.getPlatformConfig().platformId,
          limitDto: {
            pageNum: params.limitDto?.pageNum || 1,
            pageSize: params.limitDto?.pageSize || 10
          }
        })
    },
    handleCancel () {
      this.$router.back()
    },
    isValidMaxQuantity (value) {
      if (value === -1) return true
      return Number.isInteger(value) && value >= 1 && value <= 100
    },
    validateForm () {
      if (!this.form.couponCode) {
        this.$message.error('请选择派发的券')
        return false
      }
      if (this.form.userScope === 2) {
        this.$message.error('全部用户派发接口暂未开放')
        return false
      }
      if (this.form.dispatchType === 1) {
        if (!this.getUploadFile()) {
          this.$message.error('请上传领券用户附件')
          return false
        }
      } else if (this.currentSelectDialog) {
        const { idField, buttonText } = this.currentSelectDialog
        if (!this.form[idField]) {
          this.$message.error(`请${buttonText}`)
          return false
        }
      }
      if (this.form.autoDistributeType === 1 && !this.isValidMaxQuantity(this.form.maxQuantity)) {
        this.$message.error('累计最多派券次数请输入 1~100，或 -1 表示不限制')
        return false
      }
      return true
    },
    getDistributeType () {
      // 接口 distributeType: 1用户分组 2员工认证企业 3指定大学生认证 4批量上传
      const map = {
        1: 4,
        2: 1,
        3: 2,
        4: 3
      }
      return map[this.form.dispatchType]
    },
    getMaxQuantity () {
      return this.form.autoDistributeType === 0 ? 1 : Number(this.form.maxQuantity)
    },
    buildGroupPayload () {
      const payload = {
        couponCode: this.form.couponCode,
        distributeType: this.getDistributeType(),
        autoDistributeType: this.form.autoDistributeType,
        maxQuantity: this.getMaxQuantity()
      }
      if (this.currentSelectDialog) {
        payload.targetId = Number(this.form[this.currentSelectDialog.idField])
      }
      return payload
    },
    submitByImport () {
      const formData = new FormData()
      formData.append('file', this.getUploadFile())
      formData.append('couponCode', this.form.couponCode)
      formData.append('distributeType', this.getDistributeType())
      formData.append('autoDistributeType', this.form.autoDistributeType)
      formData.append('maxQuantity', this.getMaxQuantity())
      return this.requestUpload(formData)
    },
    requestUpload (formData) {
      const headers = {
        'Content-Type': 'multipart/form-data'
      }
      return this.getHttpServices()
        .getNewServeService()
        .post('/couponMember/distributeMemberImport', formData, headers)
    },
    submitByGroup () {
      return this.getHttpServices()
        .getNewServeService()
        .post('/couponMember/distributeMemberGroup', this.buildGroupPayload())
    },
    handleSubmit () {
      if (!this.validateForm()) return
      this.submitting = true
      const request = this.form.dispatchType === 1
        ? this.submitByImport()
        : this.submitByGroup()
      request
        .then(() => {
          this.$message.success('派券成功')
          this.$router.push({ name: 'CouponList' })
        })
        .catch(err => {
          this.$message.error((err && err.msg) || '派券失败，请重试')
        })
        .finally(() => {
          this.submitting = false
        })
    }
  }
}
</script>

<style scoped lang="less">
.coupon-batch-page {
  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
    margin-bottom: 20px;
  }

  .field-lg {
    max-width: 480px;
  }

  .field-scope {
    width: 140px;
    margin-right: 12px;
  }

  .field-upload-type {
    width: 200px;
    margin-right: 12px;
  }

  .template-link {
    margin-left: 12px;
  }

  .group-name {
    margin-left: 12px;
    color: #faad14;
  }

  .max-quantity-label {
    margin-left: 16px;
    margin-right: 8px;
  }

  .field-max-quantity {
    width: 120px;
  }

  .max-quantity-unit {
    margin-left: 8px;
  }

  .max-quantity-tip {
    margin-left: 8px;
    color: #999;
    font-size: 12px;
  }

  /deep/ .ant-upload {
    display: inline-block;
  }

  .footer-bar {
    padding: 16px 24px;
    background: #fff;
    text-align: right;

    .ant-btn + .ant-btn {
      margin-left: 12px;
    }
  }
  /deep/ .ant-upload-list-item-name{
    color: #faad14;
  }
}
</style>
